import { ArrowDown, FunnelSimple } from '@phosphor-icons/react'
import { colorsV4 } from '@tokenterminal/ui/theme/themeV4/colorsContract.css'
import { forwardRef, type SVGProps } from 'react'
import { monospaceFontCss } from '../../utils/typography.css'
import { Box, type BoxProps } from '../Box'
import { withOverflow } from '../util.css'
import {
  tableCell,
  tableHeadCell,
  tableRow,
  tableHeadSortIcon,
  tableHeadComplexSortIcon,
  tableStyle,
  tbody,
  thead,
  scrollableTableContainer,
  tableCellSkeleton,
  tableCellPlaceholder,
  unitStyle,
} from './Table.css'
// TODO: Move style utils to shared module? Or to UI?
/*
 * TableContainer doesn't use overflow automatically, because of CSS/HTML limitations
 * regarding sticky header and overflow. There's note about this in thead CSS, but basically:
 *
 * - Sticky head + overflow container = broken -> Eg. Use TableContainer where overflowing happens on breakpoint so sticky header is only on desktop
 * - Sticky head + max-height + overflow container = works -> Use ScrollableTableContainer
 * - Sticky head + no overflow container = works -> Use TableContainer with withOverflow
 */
export const TableContainer = forwardRef<HTMLDivElement, BoxProps>(
  (props, ref) => (
    <Box
      borderRadius="2x"
      border="1px secondary"
      maxWidth="100%"
      ref={ref}
      style={{
        borderColor: colorsV4.border.default,
      }}
      {...props}
    />
  ),
)

TableContainer.displayName = 'TableContainer'

// ScrollableTableContainer refers to container which has max height
export const ScrollableTableContainer = forwardRef<HTMLDivElement, BoxProps>(
  ({ className, ...props }: BoxProps, ref) => {
    return (
      <TableContainer
        className={[className, withOverflow, scrollableTableContainer]}
        ref={ref}
        {...props}
      />
    )
  },
)

ScrollableTableContainer.displayName = 'ScrollableTableContainer'

type Props = Omit<BoxProps, 'as'>

export const Table = forwardRef<HTMLDivElement, Props>(
  ({ className, ...props }, ref) => (
    <Box as="table" className={[tableStyle, className]} {...props} ref={ref} />
  ),
)

Table.displayName = 'Table'

export const TableHead = ({ className, ...props }: Props) => (
  <Box as="thead" className={[thead, className]} {...props} />
)

export const TableBody = ({ className, ...props }: Props) => (
  <Box as="tbody" className={[tbody, className]} {...props} />
)

export const TableRow = ({ className, ...props }: Props) => (
  <Box as="tr" className={[tableRow, className]} {...props} />
)

// Use skeleton when data is loading
export const TableCellSkeleton = ({ className, ...props }: Props) => (
  <Box
    className={[tableCellSkeleton, className]}
    aria-label="Loading data"
    aria-busy="true"
    {...props}
  />
)

// Use placeholder when the data itself is placeholder and probably paywalled.
export const TableCellPlaceholder = ({ className, ...props }: Props) => (
  <Box
    className={[tableCellPlaceholder, className]}
    aria-hidden="true"
    {...props}
  />
)

export const TableCell = ({ className, textAlign, ...props }: Props) => (
  <Box
    as="td"
    className={[tableCell, className]}
    textAlign={textAlign ?? 'right'}
    color="secondary"
    {...props}
  />
)

TableCell.displayName = 'TableCell'

export type PossibleSortKey = 'asc' | 'desc'

const ariaSorting: { [key in PossibleSortKey]: 'ascending' | 'descending' } = {
  asc: 'ascending',
  desc: 'descending',
}

export type ThCellType = Props & { isSorted?: false | PossibleSortKey }
export const ThCell = ({
  className,
  isSorted,
  textAlign,
  ...props
}: ThCellType) => (
  <Box
    as="th"
    textAlign={textAlign ?? 'right'}
    className={[tableHeadCell, className]}
    role="columnheader"
    aria-sort={(isSorted && ariaSorting[isSorted]) || undefined}
    color="secondary"
    fontWeight="500"
    {...props}
  />
)

ThCell.displayName = 'ThCell'

export const TableHeadSortIcon = ({
  variant = 'simple',
  ...props
}: Omit<SVGProps<SVGSVGElement>, 'variant'> & {
  variant?: 'simple' | 'complex'
}) => {
  switch (variant) {
    case 'complex':
      return (
        <FunnelSimple
          role="presentation"
          className={tableHeadComplexSortIcon}
        />
      )
    case 'simple':
    default:
      return (
        <ArrowDown
          className={tableHeadSortIcon}
          width="1em"
          style={{ zIndex: -1 /* thank you Mr. Safari */ }}
          // {...props}
        />
      )
  }
}

// Maybe should be in Text or via "variant" to Text. Or maybe could be just a style. Then requires something like styles/textStyles as shared
export const UnitText = (props: Omit<BoxProps, 'className'>) => (
  <Box as="span" className={unitStyle} {...props} />
)

// Percentage, ratio, price, number -values containing table cell content
export const ValueText = (props: Omit<BoxProps, 'className'>) => (
  <Box as="span" className={monospaceFontCss} {...props} />
)
